import React, { useRef, useEffect, useMemo, useState } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { useDispatch, useSelector } from 'react-redux';
import { getAllProjects } from '../redux/slice/project.slice';
import { Target, Eye, Trophy, Calendar, CheckCircle, Users, Building, ChevronDown } from 'lucide-react';
import Container from '../components/common/Container';
import AnimatedCounter from '../components/common/AnimatedCounter';
import niravBhai from "../assets/nirav bro.webp";
import ankitBhai from "../assets/ankit bro.webp";
import rajeshBhai from "../assets/Rajesh bro.webp";
import VALLETAVISTA from "../assets/VALLETA VISTA-ima.webp";
import { RevealText } from '../components/common/RevealText';

const FloatingImage = ({ src, alt, delay }) => {
    return (
        <motion.div
            initial={{ opacity: 0, y: 50, rotate: -5 }}
            whileInView={{ opacity: 1, y: 0, rotate: 0 }}
            transition={{ duration: 0.8, delay: delay, type: "spring" }}
            viewport={{ once: true }}
            className="relative"
        >
            <div className="absolute inset-0 border-2 border-secondary translate-x-4 translate-y-4 -z-10 rounded-2xl opacity-30"></div>
            <div className="relative rounded-2xl overflow-hidden shadow-2xl group">
                <div className="absolute inset-0 bg-secondary/20 group-hover:bg-transparent transition-colors duration-500 z-10"></div>
                <img src={src} alt={alt} className="w-full h-auto object-cover grayscale group-hover:grayscale-0 transition-all duration-700 transform group-hover:scale-105" />
            </div>
        </motion.div>
    );
};

const ValueCard = ({ title, description, icon }) => {
    return (
        // <motion.div
        //     whileHover={{ y: -10 }}
        //     className="bg-white p-8 rounded-2xl border border-primary/5 shadow-lg backdrop-blur-sm group hover:shadow-xl hover:border-secondary/30 transition-all duration-300"
        // >
        //     <div className="w-16 h-16 bg-secondary/10 rounded-full flex items-center justify-center mb-6 text-secondary group-hover:bg-secondary group-hover:text-white transition-all duration-300">
        //         {icon}
        //     </div>
        //     <h3 className="text-2xl font-serif font-bold text-primary mb-4">{title}</h3>
        //     <p className="text-light text-sm leading-relaxed font-sans group-hover:text-primary transition-colors">{description}</p>
        // </motion.div>

        <motion.div
            // key={index}
            variants={itemVariants}
            whileHover={{ y: -10 }}
            className="justify-center items-center flex flex-col md:items-start group md:p-6 p-4 lg:p-8 bg-surface rounded-lg border border-gray-100 shadow-sm hover:shadow-xl hover:border-secondary/30 transition-all duration-300"
        >
            <div className="mb-4 lg:mb-6  text-secondary group-hover:scale-110 transition-transform duration-300">
                {icon}
            </div>
            <h3 className="mb-2 lg:mb-3 text-md md:text-lg lg:text-xl  font-bold text-primary transition-colors">
                {title}
            </h3>
            <p className="text-secondary leading-relaxed text-sm md:text-md lg:text-lg">
                {description}
            </p>
        </motion.div>
    );
};
const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
        opacity: 1,
        y: 0,
        transition: {
            duration: 0.8,
            ease: [0.22, 1, 0.36, 1]
        }
    }
};

const statsVariants = {
    hidden: { scale: 0.8, opacity: 0 },
    visible: {
        scale: 1,
        opacity: 1,
        transition: {
            duration: 0.6,
            ease: [0.22, 1, 0.36, 1]
        }
    }
};

const TimelineItem = ({ item, index }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const isLong = item.description?.length > 150;

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ margin: "-50px" }}
            transition={{ duration: 0.6, delay: index * 0.1 }}
            className={`relative flex flex-col md:flex-row items-start md:items-center w-full ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}
        >
            {/* Content Side */}
            <div className={`w-full md:w-1/2 pl-20 md:pl-0 ${index % 2 === 0 ? 'md:pr-16 md:text-right' : 'md:pl-16 md:text-left'}`}>
                <div className="relative group">
                    <span className={`text-5xl md:text-9xl font-serif text-primary/5 font-bold absolute -top-6 md:-top-20 -z-10 select-none transition-colors duration-500 group-hover:text-primary/10 ${index % 2 === 0 ? 'left-0 md:left-auto md:right-0' : 'left-0'}`}>
                        {item.year}
                    </span>
                    <div className={`text-secondary text-sm font-bold tracking-widest mb-2 ${index % 2 === 0 ? 'md:ml-auto' : 'md:mr-auto'}`}>{item.year}</div>
                    <h3 className="text-2xl md:text-3xl font-serif font-bold text-primary mb-2 md:mb-4 group-hover:text-secondary transition-colors duration-300">{item.title}</h3>

                    <div className={`relative transition-all duration-500 ease-in-out ${!isExpanded && isLong ? 'max-h-24 overflow-hidden' : 'max-h-[1000px]'}`}>
                        <p className="text-secondary text-sm md:text-base font-sans leading-relaxed group-hover:text-primary transition-colors">
                            {item.description}
                        </p>
                        {!isExpanded && isLong && (
                            <div className="absolute bottom-0 left-0 right-0 h-10 bg-gradient-to-t from-background via-background/50 to-transparent pointer-events-none"></div>
                        )}
                    </div>

                    {isLong && (
                        <button
                            onClick={() => setIsExpanded(!isExpanded)}
                            className={`mt-4 text-secondary text-[10px] md:text-xs font-bold uppercase tracking-widest flex items-center gap-2 hover:text-primary transition-colors duration-300 ${index % 2 === 0 ? 'md:ml-auto' : ''}`}
                        >
                            {isExpanded ? 'Show Less' : 'Read More'}
                            <motion.div
                                animate={{ rotate: isExpanded ? 180 : 0 }}
                                transition={{ duration: 0.4 }}
                            >
                                <ChevronDown size={14} />
                            </motion.div>
                        </button>
                    )}
                </div>
            </div>

            {/* Center Dot */}
            <div className="absolute left-8 md:left-1/2 -translate-x-1/2 w-3 h-3 rounded-full bg-secondary border border-primary/20 z-10 shadow-[0_0_15px_rgba(134,93,54,0.6)] mt-2 md:mt-0"></div>

            {/* Empty Spacer Side */}
            <div className="hidden md:block w-1/2"></div>
        </motion.div>
    );
};

const AboutPage = () => {
    const { scrollYProgress } = useScroll();
    const y = useTransform(scrollYProgress, [0, 1], [0, -100]);
    const opacity = useTransform(scrollYProgress, [0, 0.3], [1, 0]);
    const dispatch = useDispatch();
    const { projects } = useSelector((state) => state.project);

    useEffect(() => {
        if (projects.length === 0) {
            dispatch(getAllProjects());
        }
    }, [dispatch]);

    const stats = useMemo(() => {
        const totalProjects = projects?.length || 0;
        const totalArea = projects?.reduce((acc, curr) => acc + (parseFloat(curr.projectArea) || 0), 0) || 0;

        // Calculate starting year
        let startingYear = 1999;
        if (projects && projects.length > 0) {
            const years = projects
                .map(p => p.startDate ? new Date(p.startDate).getFullYear() : null)
                .filter(y => y !== null && !isNaN(y));

            if (years.length > 0) {
                startingYear = Math.min(...years);
            }
        }

        const currentYear = new Date().getFullYear();
        const yearsOfExperience = currentYear - startingYear;

        let formattedAreaNum = 0;
        let areaSuffix = "";

        if (totalArea >= 1000000) {
            formattedAreaNum = totalArea / 1000000;
            areaSuffix = "M+";
        } else if (totalArea >= 1000) {
            formattedAreaNum = totalArea / 1000;
            areaSuffix = "K+";
        } else {
            formattedAreaNum = totalArea;
            areaSuffix = "";
        }

        const formattedArea = formattedAreaNum.toFixed(2);

        const sortedProjects = [...(projects || [])]
            .filter(p => p.startDate)
            .sort((a, b) => new Date(a.startDate) - new Date(b.startDate));

        const projectMilestones = sortedProjects.map(project => ({
            year: new Date(project.startDate).getFullYear().toString(),
            title: project.title,
            description: project.description
        }));

        const timeline = [
            {
                year: startingYear.toString(),
                title: "Inception",
                description: "Valletta Group was founded with a vision to revolutionize Surat's skyline."
            },
            ...projectMilestones
        ];

        return {
            projects: totalProjects,
            area: formattedArea,
            areaSuffix,
            startingYear,
            yearsOfExperience,
            timeline
        };
    }, [projects]);


    return (
        <div className="min-h-screen bg-background overflow-hidden text-primary selection:bg-secondary selection:text-white">

            {/* Hero Section with Parallax */}
            <section className="relative h-[80vh] flex items-center justify-center overflow-hidden">
                <motion.div
                    style={{ y, opacity }}
                    className="absolute inset-0"
                >
                    <div
                        className="absolute inset-0 bg-cover bg-center scale-110"
                        style={{ backgroundImage: "url('https://images.unsplash.com/photo-1541888946425-d81bb19240f5?q=80&w=1920&auto=format&fit=crop')" }}
                    ></div>
                    <div className="absolute inset-0 bg-white/10"></div>
                    <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-transparent"></div>
                </motion.div>

                <div className="relative z-10 text-center px-4 max-w-5xl mx-auto">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 1 }}
                    >
                        <div className="flex items-center justify-center gap-4 mb-4 md:mb-6">
                            <span className="h-px w-8 md:w-12 bg-secondary"></span>
                            <span className="text-secondary font-bold tracking-[0.2em] uppercase text-xs md:text-sm">Since {stats.startingYear}</span>
                            <span className="h-px w-8 md:w-12 bg-secondary"></span>
                        </div>
                        {/* <h1 className="text-4xl md:text-7xl lg:text-8xl font-serif font-bold text-primary mb-6 md:mb-8 leading-tight drop-shadow-sm ">
                            Building Heritage, <br />
                            <span className="text-transparent bg-clip-text bg-gradient-to-r from-secondary to-primary italic">Design Legacy</span>
                        </h1>
                        <p className="text-base md:text-xl text-primary/80 max-w-2xl mx-auto font-medium leading-relaxed px-4">
                            Crafting sustainable communities and premium living spaces that stand the test of time.
                        </p> */}
                        <h1 className="text-4xl md:text-7xl lg:text-8xl font-serif text-primary mb-6 md:mb-8 leading-tight font-bold">
                            Building Heritage, <br />
                            <span className="">Design Legacy</span>
                        </h1>
                        <p className="text-secondary text-base md:text-lg leading-relaxed max-w-2xl mx-auto font-medium">
                            Crafting sustainable communities and premium living spaces that stand the test of time.
                        </p>
                    </motion.div>
                </div>
            </section>

            {/* Main Content */}
            <section className="relative z-20 bg-background -mt-20 pt-10 lg:pt-20">
                {/* Decorative Blobs */}
                <div className="absolute top-0 left-0 w-[600px] h-[600px] bg-secondary/5 blur-[120px] rounded-full pointer-events-none" />

                <Container>
                    {/* Story Section */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-center mb-32 pt-12">
                        <motion.div
                            initial={{ opacity: 0, x: -50 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.8 }}
                            viewport={{ once: true }}
                            className="space-y-8"
                        >
                            <div>
                                <span className="text-secondary font-bold tracking-[0.2em] uppercase text-xs block mb-3">Who We Are</span>
                                <h2 className="text-4xl md:text-5xl font-serif text-primary font-bold leading-tight">The Art of <br />Fine Living</h2>
                            </div>
                            <div className="space-y-4 text-secondary text-lg leading-relaxed">
                                <p>
                                    For over <span className="text-primary font-medium">{stats.yearsOfExperience} years</span>, <span className="text-primary font-medium">Valletta Group</span> has been rewriting the narrative of real estate in Surat. We don't just build structures; we curate lifestyles.
                                </p>
                                <p>
                                    From avant-garde commercial hubs to serene residential retreats, our portfolio is a testament to our obsession with quality and detail. We have successfully delivered <span className="text-secondary">{stats.area}{stats.areaSuffix === "Sq. Ft." ? "" : stats.areaSuffix} sq. ft.</span> of constructed space, turning dreams into concrete reality.
                                </p>
                            </div>

                            {/* <div className="pt-4 grid grid-cols-2 gap-6">
                                <div className="border-l-2 border-secondary/30 pl-6">
                                    <h4 className="text-3xl font-serif font-bold text-primary mb-1"><AnimatedCounter end={stats.projects} suffix="+" /></h4>
                                    <p className="text-xs uppercase tracking-widest text-light">Completed Projects</p>
                                </div>
                                <div className="border-l-2 border-secondary/30 pl-6">
                                    <h4 className="text-3xl font-serif font-bold text-primary mb-1"><AnimatedCounter end={stats.yearsOfExperience} suffix="+" /></h4>
                                    <p className="text-xs uppercase tracking-widest text-light">Years of Trust</p>
                                </div>
                            </div> */}
                            <motion.div
                                variants={itemVariants}
                                className="pt-6 lg:pt-8 grid grid-cols-2 md:grid-cols-[auto_1fr_1fr] lg:grid-cols-2  2xl:grid-cols-[auto_1fr_1fr] gap-y-6 lg:gap-y-4 2xl:gap-y-8 gap-x-6 lg:gap-x-10 border-t border-gray-200 mt-2"
                            >
                                {/* Membership Logos */}
                                <div className="flex items-center gap-4 lg:gap-6 col-span-2 md:col-span-1 lg:col-span-2 2xl:col-span-1 border-b md:border-b-0 lg:border-b md:border-r lg:border-r-0 2xl:border-b-0 2xl:border-r border-gray-200 pb-6 md:pb-0 lg:pb-6 2xl:pb-0 md:pr-8 lg:pr-0 2xl:pr-10 shrink-0">
                                    <img src={require("../assets/credai.png")} alt="CREDAI Surat" className="h-[55px] md:h-16 object-contain hover:-translate-y-1 transition-transform duration-300 border border-secondary/30 rounded-md p-1.5 shadow-sm bg-white" onError={(e) => e.target.style.display = 'none'} />
                                    <img src={require("../assets/seba.png")} alt="SEBA Surat" className="h-[55px] md:h-16 object-contain hover:-translate-y-1 transition-transform duration-300 border border-secondary/30 rounded-md p-1.5 shadow-sm bg-white" onError={(e) => e.target.style.display = 'none'} />
                                </div>

                                {/* Stat 1 */}
                                <motion.div variants={statsVariants} className="flex flex-col justify-center border-r border-gray-200 pr-4  lg:pr-8 whitespace-nowrap ">
                                    <h3 className="text-3xl md:text-3xl lg:text-4xl font-serif text-primary font-bold mb-1 leading-none">
                                        <AnimatedCounter end={stats.projects || 14} suffix="+" />
                                    </h3>
                                    <p className="text-secondary/80 font-medium text-sm tracking-wide">Projects</p>
                                </motion.div>

                                {/* Stat 2 */}
                                <motion.div variants={statsVariants} className="flex flex-col justify-center whitespace-nowrap pl-2 md:pl-0">
                                    <h3 className="text-3xl md:text-3xl lg:text-4xl font-serif text-primary font-bold mb-1 leading-none">
                                        <AnimatedCounter end={stats.area || 3} suffix={stats.area ? stats.areaSuffix : "M+"} decimals={2} />
                                    </h3>
                                    <p className="text-secondary/80 font-medium text-sm tracking-wide">Sq. Ft. Delivered</p>
                                </motion.div>
                            </motion.div>
                        </motion.div>

                        <FloatingImage
                            src={VALLETAVISTA}
                            alt="Corporate Office"
                            delay={0.2}
                        />
                    </div>

                    {/* Mission Vision Values - 3D Cards */}
                    <div className="mb-40">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            className="text-center mb-16 max-w-3xl mx-auto"
                        >
                            <span className="text-secondary font-bold tracking-[0.2em] uppercase text-xs block mb-4">Core Philosophy</span>
                            <h2 className="text-4xl md:text-5xl font-serif font-bold text-primary mb-6">Built on Values</h2>
                            <p className="text-secondary leading-relaxed lg:text-lg md:text-base text-sm  font-sans font-medium">Our foundation is stronger than concrete, it's built on a commitment to excellence, integrity, and customer delight.</p>
                        </motion.div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                            <ValueCard
                                icon={<Target size={32} />}
                                title="Our Mission"
                                description="To grow as a company and a community known for innovation, architectural brilliance, efficiency, and professionalism with a high-spirited level of dedication."
                            />
                            <ValueCard
                                icon={<Eye size={32} />}
                                title="Our Vision"
                                description="To deliver ultimate customer delight and highest quality of living space solutions for consumers, aiming to be a leading developer with a PAN India presence."
                            />
                            <ValueCard
                                icon={<Trophy size={32} />}
                                title="Our Objective"
                                description="To provide premium housing spaces and commercial complexes at par with international standards, enabling a luxurious lifestyle at an affordable rate."
                            />
                        </div>
                    </div>

                    {/* Leadership Spotlight Section */}
                    <div className="mb-40">
                        <div className="flex flex-col md:flex-row justify-between items-center md:items-end mb-16 gap-8">
                            <motion.div
                                initial={{ opacity: 0, x: -30 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.8 }}
                            >
                                <span className="text-secondary font-bold tracking-[0.2em] uppercase text-md block mb-4">
                                    Leadership
                                </span>
                                {/* <h2 className="text-4xl md:text-5xl font-serif font-bold text-primary max-w-lg">
                                    The Visionaries <br />Behind Valletta
                                </h2> */}
                                <h2 className="text-3xl md:text-4xl lg:text-5xl  font-serif text-primary font-bold leading-tight">
                                    <RevealText text="The Visionaries" className='leading-tight' />
                                    <span className=" block mt-2 leading-tight">Behind Valletta</span>
                                </h2>
                            </motion.div>

                            <motion.p
                                initial={{ opacity: 0, x: 30 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.8, delay: 0.2 }}
                                className="text-secondary font-sans font-medium text-base max-w-md text-center md:text-left"
                            >
                                Guided by experience, driven by passion. Our leadership team brings together decades of industry expertise.
                            </motion.p>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-12">
                            {[
                                {
                                    name: "Nirav Maniya",
                                    role: "Chairman",
                                    img: niravBhai
                                },
                                {
                                    name: "Rajesh Jasani",
                                    role: "Managing Director",
                                    img: rajeshBhai
                                },
                                {
                                    name: "Ankit Patel",
                                    role: "Technical Director",
                                    img: ankitBhai
                                }
                            ].map((leader, index) => (
                                <motion.div
                                    key={index}
                                    initial={{ opacity: 0, y: 30 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.5, delay: index * 0.2 }}
                                    className="group"
                                >
                                    <div className="relative overflow-hidden rounded-2xl mb-6 aspect-[4/5]">
                                        <div className="absolute inset-0 bg-gradient-to-t from-black to-transparent opacity-80 z-10 transition-opacity duration-300 group-hover:opacity-70"></div>

                                        <img
                                            src={leader.img}
                                            alt={leader.name}
                                            className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700 transform scale-100 group-hover:scale-105"
                                        />

                                        <div className="absolute bottom-0 left-0 right-0 p-6 z-20 translate-y-4 group-hover:translate-y-0 transition-transform duration-300">
                                            <div className="h-0.5 w-12 bg-secondary mb-4 scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left"></div>

                                            <h3 className="text-2xl font-serif font-bold text-white mb-1 transition-colors">
                                                {leader.name}
                                            </h3>

                                            {/* <p className="text-accent uppercase tracking-widest text-xs font-bold">
                                                {leader.role}
                                            </p> */}
                                        </div>
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    </div>

                    {/* Timeline / History Section */}
                    <div className="mb-40 relative">
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8 }}
                            className="text-center mb-24"
                        >
                            <span className="text-secondary font-bold tracking-[0.2em] uppercase text-sm block mb-4">Our Journey</span>
                            <h2 className="text-4xl md:text-5xl font-serif font-bold text-primary">Milestones of Excellence</h2>
                        </motion.div>

                        <div className="relative">
                            {/* Center Line for Desktop */}
                            <div className="absolute left-8 md:left-1/2 top-0 bottom-0 w-[2px] bg-gradient-to-b from-transparent via-primary/50 to-transparent md:-translate-x-1/2"></div>

                            <div className="space-y-12 md:space-y-20">
                                {stats.timeline?.map((item, index) => (
                                    <TimelineItem key={index} item={item} index={index} />
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Sustainability Section */}
                    <div className="mb-40 bg-white rounded-[1rem] md:rounded-[3rem] p-8 md:p-16 border border-primary/5 shadow-2xl overflow-hidden relative">
                        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-secondary/5 blur-[150px] rounded-full pointer-events-none"></div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center relative z-10">
                            <motion.div
                                initial={{ opacity: 0, x: -50 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.8 }}
                                className="space-y-8"
                            >
                                <div>
                                    <span className="text-secondary font-bold tracking-[0.2em] uppercase text-sm block mb-4">Eco-Friendly Living</span>
                                    <h2 className="text-3xl md:text-5xl font-serif font-bold text-primary leading-snug">Committed to <br />Sustainability</h2>
                                </div>
                                <p className="text-lg text-secondary leading-relaxed font-sans">
                                    At Valletta Group, we believe that luxury should not come at the cost of the environment. Our latest projects incorporate cutting-edge green technologies to minimize carbon footprint while maximizing comfort.
                                </p>
                                <ul className="space-y-3">
                                    {[
                                        "IGBC Gold Rated Green Buildings",
                                        "Solar Power Integration for Common Areas",
                                        "Advanced Rainwater Harvesting Systems",
                                        "Use of Sustainable, Locally Sourced Materials"
                                    ].map((item, i) => (
                                        <li key={i} className="flex items-center text-secondary text-md tracking-wide">
                                            <div className="w-8 h-8 rounded-full bg-primary/5 flex items-center justify-center mr-4 text-green-600 border border-primary/10 shrink-0">
                                                <CheckCircle size={14} />
                                            </div>
                                            {item}
                                        </li>
                                    ))}
                                </ul>
                            </motion.div>

                            <FloatingImage
                                src="https://images.unsplash.com/photo-1542601906990-b4d3fb778b09?auto=format&fit=crop&q=80"
                                alt="Sustainability"
                                delay={0.2}
                            />
                        </div>
                    </div>
                </Container>
            </section>
        </div>
    );
};

export default AboutPage;
